import './ApplianceReviews.scss';

import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import FsLightbox from 'fslightbox-react';
import _ from 'lodash';

import { Input } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { useCreateReviewAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/reviews';
import { useGetReviewRatingsMetadataQuery } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/reviews/queries';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { Rating } from '@sbi-international/boutik.gatsby.plugin.components';
import {
    ProductReviews,
    ProductReviewsReview,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/models/productReviews';
import { useOtherBoutikService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useOtherBoutikService';
import { ReviewsService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/interfaces/services/reviewsService';
import { getBase64, removeWhiteSpaces, scrollToRef } from '../../utils/index';
import DroletButton from '../Primitives/DroletButton';
import arrowIcon from '../../images/icons/Icon-Fleche-Black.svg';
import attachIcon from '../../images/icons/picto-attach.png';

function SingleReview(props: {
    review: ProductReviewsReview;
    index: number;
    seeMore: number;
    reviewImgUrls: Record<string, string>;
}) {
    const { review, index, seeMore, reviewImgUrls } = props;
    const { nickname, text, rating, created_at } = review;
    const descriptionRef = useRef<HTMLSpanElement>(null);
    const [isLightBoxVisible, setIsLightBoxVisible] = useState(false);
    const dateFormat = created_at ? created_at.split(' ')[0] : '--';

    const reviewsService: ReviewsService =
        useOtherBoutikService('ReviewsService');

    const reviewImage =
        reviewImgUrls[reviewsService.generateImageReviewId(review)];

    return (
        <div
            className='review'
            style={{
                display:
                    (seeMore > 0 && index < seeMore) || index < 7
                        ? 'block'
                        : 'none',
            }}
        >
            <div className='review-head'>
                <span className='rating'>
                    <Rating.RatingScore ratingScore={rating} />
                </span>
                <span className='reviewer'>{nickname}</span>
                <span className='date-created'>{dateFormat}</span>
            </div>
            <span ref={descriptionRef} className='description'>
                {text}
            </span>
            {reviewImage && (
                <>
                    <img
                        className='review-img'
                        src={reviewImage}
                        alt=''
                        onClick={() => setIsLightBoxVisible(!isLightBoxVisible)}
                    />
                    <FsLightbox
                        toggler={isLightBoxVisible}
                        sources={[reviewImage]}
                        exitFullscreenOnClose={true}
                    />
                </>
            )}
        </div>
    );
}

function AddReviewForm(props: any) {
    const { sku, formReff } = props;
    const [errors, setErrors] = useState({
        name: '',
        comment: '',
        starRating: '',
    });
    const DEFAULT_STAR_NUMB = 0;
    const [showErrMessage, setShowErrorMessages] = useState(false);
    const [name, setName] = useState('');
    const [comment, setComment] = useState('');
    const [newRating, setNewRating] = useState(DEFAULT_STAR_NUMB);
    const [reviewAttachments, setReviewAttachments]: [
        string | undefined,
        React.Dispatch<React.SetStateAction<any>>
    ] = useState(undefined);
    const { t } = useTranslation('layout');
    const [createReview] = useCreateReviewAction();
    const { addAlert } = useContext(feedbackContext);
    const addForm = useRef(null);
    useEffect(() => {
        if (
            errors?.comment.trim() !== '' &&
            errors?.name.trim() !== '' &&
            errors?.starRating.trim() !== ''
        ) {
            setShowErrorMessages(true);
        }
    }, [errors]);
    const reviewsMetadata =
        useGetReviewRatingsMetadataQuery()[0]?.productReviewRatingsMetadata
            ?.items[0];

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value.trim() !== '') {
                isValid = false;
                break;
            }
        }

        if (name.trim() === '') {
            setErrors((errors) => ({ ...errors, name: 'error' }));
            isValid = false;
        }

        if (comment.trim() === '') {
            setErrors((errors) => ({ ...errors, comment: 'error' }));
            isValid = false;
        }

        if (newRating === 0) {
            setErrors((errors) => ({ ...errors, starRating: 'error' }));
            isValid = false;
        }
        return isValid;
    }, [comment, errors, name, newRating]);

    const onSubmit = useCallback(
        async (event: any) => {
            event.preventDefault();
            if (isFormValid()) {
                await createReview({
                    sku: sku,
                    nickname: name,
                    summary: 'empty',
                    text: comment,
                    ratings: [
                        {
                            id: reviewsMetadata?.id as string,
                            value_id: reviewsMetadata?.values[newRating - 1]
                                ?.value_id as string,
                        },
                    ],
                })
                    .then(async (data) => {
                        if (reviewAttachments) {
                            const b64ObjType = (
                                reviewAttachments as string
                            ).split(`,`)[0];
                            let type = 'image/jpeg';
                            if (b64ObjType.includes('png')) {
                                type = 'image/png';
                            } else if (b64ObjType.includes('jpg')) {
                                type = 'image/jpg';
                            }
                            const reviewObj =
                                data?.data?.createProductReview?.review;
                            const reviewId = generateReviewId(
                                reviewObj?.nickname as string,
                                reviewObj?.created_at as string,
                                sku
                            );
                            const requestOptions = {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    reviewId: reviewId,
                                    sku: sku,
                                    nickname: reviewObj?.nickname,
                                    site: 'drolet.com',
                                    created_at: reviewObj?.created_at,
                                    filedata: reviewAttachments,
                                    type: type,
                                }),
                            };
                            await fetch(`/inquiry/reviews`, requestOptions);
                        }
                    })
                    .then(() => {
                        event.target.reset();
                        setName('');
                        setComment('');
                        setNewRating(DEFAULT_STAR_NUMB);
                        addAlert({
                            message: t('toastMessages:review-message'),
                            status: 'success',
                        });
                    })
                    .catch((error: any) => {
                        addAlert({ message: error, status: 'error' });
                        console.error(error.message);
                    });
            }
        },
        [
            addAlert,
            comment,
            createReview,
            isFormValid,
            name,
            newRating,
            reviewsMetadata?.id,
            reviewsMetadata?.values,
            sku,
            reviewAttachments,
            t,
        ]
    );

    const attachPhoto = () => {
        const fileInput = document.getElementById('review-fileinput');
        if (fileInput) {
            fileInput.click();
        }
    };

    const uploadPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentFiles = event?.target?.files;
        if (currentFiles && currentFiles?.length > 0) {
            const base64 = await getBase64(currentFiles[0]);
            setReviewAttachments(base64);
        }
    };
    return (
        <div ref={formReff}>
            {props.reviewOpen && (
                <form
                    onSubmit={(event) => onSubmit(event)}
                    className='review-form'
                    ref={addForm}
                >
                    <div className='inputGroup row'>
                        <div className='input-row'>
                            <label htmlFor='firstname' className='label'>
                                {t('review.firstname')}
                            </label>
                            <Input
                                id='firstname'
                                className='review-input'
                                type='text'
                                value={name}
                                onChange={(value: string) => {
                                    setErrors((errors) => ({
                                        ...errors,
                                        name: '',
                                    }));
                                    setName(value);
                                }}
                            />
                        </div>
                        <div className='input-row'>
                            <span className='label'>
                                {t('review.global-note')}
                            </span>
                            <div
                                className={
                                    errors.starRating === 'error'
                                        ? 'review-starRating error'
                                        : 'review-starRating'
                                }
                            >
                                <span
                                    className={
                                        newRating === 5
                                            ? 'review-star selected'
                                            : 'review-star '
                                    }
                                    onClick={() => {
                                        setNewRating(5);
                                        setErrors((errors) => ({
                                            ...errors,
                                            starRating: '',
                                        }));
                                    }}
                                />
                                <span
                                    className={
                                        newRating === 4
                                            ? 'review-star  selected'
                                            : 'review-star '
                                    }
                                    onClick={() => {
                                        setNewRating(4);
                                        setErrors((errors) => ({
                                            ...errors,
                                            starRating: '',
                                        }));
                                    }}
                                />
                                <span
                                    className={
                                        newRating === 3
                                            ? 'review-star  selected'
                                            : 'review-star '
                                    }
                                    onClick={() => {
                                        setNewRating(3);
                                        setErrors((errors) => ({
                                            ...errors,
                                            starRating: '',
                                        }));
                                    }}
                                />
                                <span
                                    className={
                                        newRating === 2
                                            ? 'review-star  selected'
                                            : 'review-star '
                                    }
                                    onClick={() => {
                                        setNewRating(2);
                                        setErrors((errors) => ({
                                            ...errors,
                                            starRating: '',
                                        }));
                                    }}
                                />
                                <span
                                    className={
                                        newRating === 1
                                            ? 'review-star  selected'
                                            : 'review-star '
                                    }
                                    onClick={() => {
                                        setNewRating(1);
                                        setErrors((errors) => ({
                                            ...errors,
                                            starRating: '',
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='inputGroup'>
                        <label className='label' htmlFor='comment-text'>
                            {t('review.comment')}
                        </label>
                        <textarea
                            id='comment-text'
                            className='comment'
                            onChange={(event) => {
                                setComment(event.currentTarget.value);
                                setErrors((errors) => ({
                                    ...errors,
                                    comment: '',
                                }));
                            }}
                        />
                    </div>
                    {showErrMessage && (
                        <div className='error-desc'>
                            {getErrorMessage(errors, t)}
                        </div>
                    )}
                    <div className='add-an-image'>
                        <form action='' style={{ display: 'none' }}>
                            <input
                                type='file'
                                accept='.jpg, .jpeg, .png'
                                onChange={(e) => uploadPhoto(e)}
                                id='review-fileinput'
                            />
                        </form>
                        <img
                            src={attachIcon}
                            alt=''
                            onClick={attachPhoto}
                        ></img>
                        <span onClick={attachPhoto}>
                            {t('review.add-image')}
                        </span>
                    </div>
                    {reviewAttachments && (
                        <img
                            src={reviewAttachments}
                            alt=''
                            className='review-img-preview'
                        />
                    )}
                    <div className='btn-wrapper'>
                        <DroletButton
                            label={t('review.send')}
                            type='submit'
                        ></DroletButton>
                    </div>
                </form>
            )}
        </div>
    );
}

const ApplianceReviews = React.forwardRef(function ApplianceReviewsRef(
    props: {
        code: string;
    },
    ref: any
) {
    const reviewListing = useStaticQuery(
        graphql`
            query {
                allProductsJson(
                    filter: { code: { eq: "drolet_review-appliance" } }
                ) {
                    nodes {
                        title {
                            en_CA
                            en_US
                            fr_CA
                        }
                        attributes {
                            content {
                                values {
                                    en_CA
                                    en_US
                                    fr_CA
                                }
                            }
                        }
                    }
                }
            }
        `
    );
    const [reviewOpen, setReviewOpen] = useState(false);
    const reviewData = reviewListing?.allProductsJson?.nodes[0];
    const { code } = props;

    const reviewsService: ReviewsService =
        useOtherBoutikService('ReviewsService');

    const [productReviews, setProductReviews] = useState<ProductReviews>();
    const [reviewImgUrls, setReviewImgUrls] = useState<Record<string, string>>(
        {}
    );

    const [filteredReviews, setFilteredReviews] = useState<
        ProductReviewsReview[]
    >([]);

    useEffect(() => {
        reviewsService
            .getReviewsBySku(code)
            .then((reviewsObject) => {
                setProductReviews(reviewsObject as ProductReviews);
                setFilteredReviews(
                    reviewsObject?.reviews
                        ? (reviewsObject?.reviews as ProductReviewsReview[])
                        : []
                );
            })
            .catch((e) => console.error(e));
        reviewsService
            .getImageReviewUrls(code)
            .then((urls) => {
                setReviewImgUrls(urls);
            })
            .catch((e) => console.error(e));
    }, []);

    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const [seeMore, setSeeMore] = useState(0);
    const [buttonLabel, setButtonLabel] = useState(
        t('review.read-more') as string
    );
    const formReff = useRef(null);

    return (
        <div ref={ref} className='appliance-reviews'>
            <div className='appliance-reviews-header'>
                <span className='review-title'>
                    {t('review.what-clients-think')}
                </span>
                <span className='review-title big'>{t('review.comments')}</span>
                <span className='line'></span>
            </div>
            <div className='review-summary'>
                <div className='block-display'>
                    <div className='sumarry-row'>
                        <div className='title-row more-flex'>
                            <span className='review-title'>
                                {reviewData?.title?.[currentLocale]}
                            </span>
                            <span className='line'></span>
                            <span className='content'>
                                {
                                    reviewData?.attributes?.content
                                        ?.values[0]?.[currentLocale]
                                }
                            </span>
                        </div>
                        <DroletButton
                            label={t('review.add-comment')}
                            onClick={() => {
                                setReviewOpen(!reviewOpen);
                                scrollToRef(formReff, 140);
                            }}
                        ></DroletButton>
                    </div>

                    <div className='sumarry-row align-start'>
                        <div className='dark-stars'>
                            <Rating.RatingScore
                                ratingScore={productReviews?.average ?? 0}
                            />
                        </div>
                        <div className='row-section reviews-stats'>
                            {renderSumarry(filteredReviews)}
                        </div>
                    </div>
                </div>
                <AddReviewForm
                    sku={code}
                    reviewOpen={reviewOpen}
                    formReff={formReff}
                />

                <div className='reviews'>
                    {filteredReviews?.length > 0 && (
                        <div className='review-filter'>
                            <ReviewFilter
                                filteredReviews={filteredReviews}
                                setFilteredReviews={setFilteredReviews}
                            />
                        </div>
                    )}
                    {filteredReviews && filteredReviews?.length > 0 ? (
                        filteredReviews?.map(
                            (review: ProductReviewsReview, index: number) => {
                                return (
                                    <React.Fragment key={index}>
                                        <SingleReview
                                            review={review}
                                            index={index}
                                            seeMore={seeMore}
                                            reviewImgUrls={reviewImgUrls}
                                        />
                                    </React.Fragment>
                                );
                            }
                        )
                    ) : (
                        <p className='no-review'>{t('review.no-reviews')}</p>
                    )}
                </div>
                {filteredReviews.length > 7 &&
                    filteredReviews.length >= seeMore && (
                        <div className='see-more'>
                            <DroletButton
                                label={buttonLabel}
                                onClick={() => {
                                    setSeeMore(seeMore + 25); //Add up to 25 more reviews to the page at a time
                                    //if (!seeMore) {
                                    //    setButtonLabel(t('review.read-less'));
                                    //} else {
                                    setButtonLabel(t('review.read-more'));
                                    //}
                                }}
                            ></DroletButton>
                        </div>
                    )}
            </div>
        </div>
    );
});

const renderSumarry = (reviews: ProductReviewsReview[]) => {
    // eslint-disable-next-line prefer-const
    let reviewMap = new Map();
    reviewMap.set('1', 0);
    reviewMap.set('2', 0);
    reviewMap.set('3', 0);
    reviewMap.set('4', 0);
    reviewMap.set('5', 0);
    if (reviews) {
        reviews.forEach((review: ProductReviewsReview) => {
            const rating = review.rating.toString();
            const oldValue = reviewMap.get(rating);
            if (oldValue) {
                reviewMap.set(rating, oldValue + 1);
            } else {
                reviewMap.set(rating, 1);
            }
        });
    }

    const total =
        reviewMap.get('1') +
            reviewMap.get('2') +
            reviewMap.get('3') +
            reviewMap.get('4') +
            reviewMap.get('5') || 1;

    let sectionNb = 6;
    return (
        <div className='review-by-numb'>
            {_.times(5, () => {
                sectionNb = sectionNb - 1;

                return (
                    <span className='single-sum-row' key={sectionNb}>
                        <span className='star-number'>{sectionNb} &#9733;</span>
                        <div className='percentage-container'>
                            <div
                                className='percentage'
                                style={{
                                    width: `${
                                        (reviewMap.get(sectionNb.toString()) /
                                            total) *
                                        100
                                    }%`,
                                }}
                            ></div>{' '}
                        </div>
                        <span className='review-number'>
                            {reviewMap.get(sectionNb.toString())}
                        </span>
                    </span>
                );
            })}
        </div>
    );
};

export default ApplianceReviews;
function getErrorMessage(
    errors: { name: string; comment: string; starRating: string },
    t: any
): string {
    if (errors?.name.trim() !== '') {
        return t('layout:review.name-error');
    }
    if (errors?.comment.trim() !== '') {
        return t('layout:review.comment-error');
    }
    if (errors?.starRating.trim() !== '') {
        return t('layout:review.star-error');
    }
    return '';
}
function ReviewFilter(props: {
    setFilteredReviews: React.Dispatch<React.SetStateAction<any[]>>;
    filteredReviews: any[];
}) {
    const [showFilter, setShowFilter] = useState(false);
    const { t } = useTranslation('layout');
    const { filteredReviews, setFilteredReviews } = props;
    const [currentFilterType, setCurrentFilterType]: [
        string,
        React.Dispatch<React.SetStateAction<string>>
    ] = useState(t('review.filter.most-recent'));
    const { currentLocale } = usePageLocalization();
    const normal = 'review-filter-elem';
    const selected = 'review-filter-elem filter-selected';
    const getClassName = (val: string) => {
        if (val === currentFilterType) return selected;
        return normal;
    };
    const applyFilter = (filterTitle: string, updatedReviews: any[]) => {
        setCurrentFilterType(filterTitle);
        setShowFilter(false);
        if (updatedReviews) setFilteredReviews([...updatedReviews]);
    };
    useEffect(() => {
        const filterTitle =
            currentLocale === 'fr_CA' ? 'Plus récents' : 'Most recent';
        setCurrentFilterType(filterTitle);
        setShowFilter(false);
    }, [currentLocale]);
    return (
        <div
            className='review-filter-component'
            onClick={() => setShowFilter(!showFilter)}
        >
            <div className='review-filter-title'>
                <span>{t('appliance.filter.sort-by') + currentFilterType}</span>
                <img
                    className={`iconRight`}
                    src={arrowIcon}
                    alt={''}
                    style={showFilter ? { transform: 'rotate(90deg)' } : {}}
                />
            </div>
            <div
                className='review-filter-list'
                style={{ display: showFilter ? 'block' : 'none' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={getClassName(t('review.filter.most-recent'))}
                    onClick={() => {
                        const updated = filteredReviews
                            ?.slice()
                            ?.sort(
                                (a, b) =>
                                    +new Date(b?.created_at) -
                                    +new Date(a?.created_at)
                            );
                        applyFilter(t('review.filter.most-recent'), updated);
                    }}
                >
                    <span>{t('review.filter.most-recent')}</span>
                </div>
                <div
                    className={getClassName(t('review.filter.oldest-first'))}
                    onClick={() => {
                        const updated = filteredReviews
                            ?.slice()
                            ?.sort(
                                (a, b) =>
                                    +new Date(a?.created_at) -
                                    +new Date(b?.created_at)
                            );
                        applyFilter(t('review.filter.oldest-first'), updated);
                    }}
                >
                    <span>{t('review.filter.oldest-first')}</span>
                </div>
                <div
                    className={getClassName(t('review.filter.rating-high'))}
                    onClick={() => {
                        const updated = filteredReviews
                            ?.slice()
                            ?.sort((a, b) => b?.rating - a?.rating);
                        applyFilter(t('review.filter.rating-high'), updated);
                    }}
                >
                    <span>{t('review.filter.rating-high')}</span>
                </div>
                <div
                    className={getClassName(t('review.filter.rating-low'))}
                    onClick={() => {
                        const updated = filteredReviews
                            ?.slice()
                            ?.sort((a, b) => a?.rating - b?.rating);
                        applyFilter(t('review.filter.rating-low'), updated);
                    }}
                >
                    <span>{t('review.filter.rating-low')}</span>
                </div>
            </div>
        </div>
    );
}

function generateReviewId(
    reviewer: string,
    dateCreated: string,
    sku: string
): string {
    const formatedDate = moment(dateCreated).format('YYYYMMDD');
    return removeWhiteSpaces(`${sku}-${reviewer}-${formatedDate}`);
}
